import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { showNotificationGlobal } from "../common/NotificationProvider";
import { messages } from "../../settings/shared/CommonMessages";
import { ACCESS_TOKEN, API_RESPONSE, CODER_MAX_FILE_SIZE_UPLOAD, CODER_MAX_FILES_UPLOAD, defaultCharacterLimit } from "../../settings/shared/constants";
import StackService from "../../services/api/stackservice/index";
import ColorPaletteSelector from "./ColorPaletteSelector";
import PremiumFeatureLocked from "../common/PremiumFeatureLocked";
import StackSelector from "./StackSelector";
import { clearFindingChatDraft, MAIN_CHAT_SET_DRAFT_EVENT, takeFindingChatDraft } from "../../settings/shared/findingChatDraft";
import "../../assets/css/chat/chatComponent.css";
import { isPocTenant } from "../../utils/premiumAccess";


const CODEVIEW_FILES_EVENT = "codeview-files-updated";
const MAIN_CHAT_SUBMIT_EVENT = "main-chat-submit";
const MAIN_CHAT_INSERT_CODE_EVENT = "main-chat-insert-code";

/** Values allowed in the chat composer app-type dropdown (must match APP_TYPES_SMALL). */
const TEXTAREA_APP_TYPE_VALUES = new Set([
    "APP",
    "APP_AGENT",
    "AGENT",
    "PLAN",
    "WEBSITE",
    "ASK",
]);

/** @returns {{ open: boolean, query?: string, atIndex?: number }} */
function getCodeMentionState(value, caret) {
    if (caret == null || caret < 0) return { open: false };
    const before = value.slice(0, caret);
    const lineStart = before.lastIndexOf("\n") + 1;
    const line = before.slice(lineStart);
    const atIdx = line.lastIndexOf("@");
    if (atIdx === -1) return { open: false };
    const afterAt = line.slice(atIdx + 1);
    if (/\s/.test(afterAt)) return { open: false };
    return { open: true, query: afterAt, atIndex: lineStart + atIdx };
}

const TextareaInput = ({
    onSubmit,
    onStop,
    isStreaming = false,
    placeholder = "Plan, search, build apps",
    onSendClick,
    enableAtMentions = false,
    /** When set (e.g. from loaded project), drives the app-type dropdown label and submits. */
    projectAppType,
    onAppTypeChange,
    findingChatDraft = null,
}) => {
    const navigate = useNavigate();
    const [input, setInput] = useState("");
    const [selectedAppType, setSelectedAppType] = useState("PLAN");
    const [premiumDialog, setPremiumDialog] = useState({ show: false, featureName: "" });
    const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]); // Array of { file, preview, type: 'image' | 'pdf' }
    const [isRecording, setIsRecording] = useState(false);
    const [selectedPalette, setSelectedPalette] = useState(null);
    const [isAppTypeMenuOpen, setIsAppTypeMenuOpen] = useState(false);
    const [stacks, setStacks] = useState([]);
    const [stacksLoading, setStacksLoading] = useState(false);
    const [isStackMenuOpen, setIsStackMenuOpen] = useState(false);
    const [selectedStackId, setSelectedStackId] = useState("");
    const [stacksError, setStacksError] = useState(false); 
    const stackMenuRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const recognitionRef = useRef(null);
    const accumulatedTranscriptRef = useRef("");
    const isRecordingRef = useRef(false);
    const baseInputRef = useRef(""); // Track input before voice recording
    const lastInterimRef = useRef(""); // Track last interim text to replace it
    const [codeViewFiles, setCodeViewFiles] = useState([]);
    const [mentionStart, setMentionStart] = useState(null);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionActiveIdx, setMentionActiveIdx] = useState(0);
    const [selectedMentionTokens, setSelectedMentionTokens] = useState([]); // array of exact tokens in input, e.g. "@src/a.js" or "@src/a.js (1-2)"
    const mentionDropdownRef = useRef(null);
    const handleSubmitRef = useRef(null);
    const highlightLayerRef = useRef(null);
    const shouldResetHeightRef = useRef(false);
    const pendingXraySubmitRef = useRef(null);
    const appliedFindingDraftRef = useRef("");

    const codeViewPaths = useMemo(
        () =>
            [...codeViewFiles.map((f) => f.name).filter(Boolean)].sort((a, b) =>
                a.localeCompare(b)
            ),
        [codeViewFiles]
    );

    const mentionSuggestions = useMemo(() => {
        const q = (mentionQuery || "").toLowerCase();
        const list = !q
            ? codeViewPaths
            : codeViewPaths.filter((p) => p.toLowerCase().includes(q));
        return list.slice(0, 100);
    }, [mentionQuery, codeViewPaths]);

    const highlightParts = useMemo(() => {
        if (!enableAtMentions) return [{ type: "text", value: input || "" }];
        const text = input || "";
        const re = /@([^\s]+)(\s*\(\s*(\d+)\s*-\s*(\d+)\s*\))?/g;
        let last = 0;
        const parts = [];
        for (let m = re.exec(text); m; m = re.exec(text)) {
            const start = m.index;
            const token = m[0];
            const end = start + token.length;
            if (start > last) parts.push({ type: "text", value: text.slice(last, start) });
            const filePath = m[1] || "";
            const startLine = m[3];
            const endLine = m[4];
            const hasRange = Boolean(startLine && endLine);
            const showAsBadge =
                hasRange || selectedMentionTokens.includes(token);
            if (showAsBadge) {
                // Keep label identical to token so caret width never drifts.
                parts.push({ type: "mention", value: token, label: token, start, end });
            } else {
                // While user is typing "@query", keep it plain text.
                parts.push({ type: "text", value: token });
            }
            last = end;
        }
        if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
        return parts.length ? parts : [{ type: "text", value: text }];
    }, [input, enableAtMentions, selectedMentionTokens]);

    useEffect(() => {
        if (!enableAtMentions) return;
        // Drop tokens that no longer exist in the input (e.g. user deleted them).
        setSelectedMentionTokens((prev) => prev.filter((t) => input.includes(t)));
    }, [input, enableAtMentions]);

    const syncMentionFromCaret = (value, caret) => {
        if (!enableAtMentions) return;
        const m = getCodeMentionState(value, caret);
        if (!m.open) {
            setMentionStart(null);
            setMentionQuery("");
            setMentionActiveIdx(0);
            return;
        }
        setMentionStart(m.atIndex);
        setMentionQuery(m.query);
    };

    useEffect(() => {
        if (!enableAtMentions) return;
        if (mentionStart == null) return;
        setMentionActiveIdx(0);
    }, [mentionQuery, mentionStart, enableAtMentions]);

    useEffect(() => {
        if (!enableAtMentions) return;
        const onFiles = (e) => {
            const files = e?.detail?.files;
            setCodeViewFiles(Array.isArray(files) ? files : []);
        };
        window.addEventListener(CODEVIEW_FILES_EVENT, onFiles);
        return () => window.removeEventListener(CODEVIEW_FILES_EVENT, onFiles);
    }, [enableAtMentions]);

    useEffect(() => {
        const onMainChatSubmit = () => {
            handleSubmitRef.current?.({ preventDefault: () => {} });
        };
        window.addEventListener(MAIN_CHAT_SUBMIT_EVENT, onMainChatSubmit);
        return () => window.removeEventListener(MAIN_CHAT_SUBMIT_EVENT, onMainChatSubmit);
    }, []);

    useEffect(() => {
        if (projectAppType == null || projectAppType === "") return;
        const normalized = String(projectAppType).toUpperCase();
        if (TEXTAREA_APP_TYPE_VALUES.has(normalized)) {
            setSelectedAppType(normalized);
        }
    }, [projectAppType]);

    useEffect(() => {
        const onInsertCode = (e) => {
            const file = e?.detail?.file;
            const startLine = e?.detail?.startLine;
            const endLine = e?.detail?.endLine;
            if (!file || !startLine || !endLine) return;

            const token = `@${file} (${startLine} - ${endLine})`;
            // Insert only the mention + line range (no full code text).
            const insertText = `${token}\n`;
            const ta = textareaRef.current;

            if (ta) {
                const start = ta.selectionStart ?? input.length;
                const end = ta.selectionEnd ?? start;
                ta.focus();
                ta.setRangeText(insertText, start, end, "end");
                setInput(ta.value);
                setSelectedMentionTokens((prev) => (prev.includes(token) ? prev : [...prev, token]));
                requestAnimationFrame(() => {
                    if (!textareaRef.current) return;
                    const pos = start + insertText.length;
                    textareaRef.current.selectionStart = pos;
                    textareaRef.current.selectionEnd = pos;
                    syncMentionFromCaret(textareaRef.current.value, pos);
                });
                return;
            }

            setInput((prev) => `${prev}${prev ? "\n" : ""}${insertText}`);
            setSelectedMentionTokens((prev) => (prev.includes(token) ? prev : [...prev, token]));
        };

        window.addEventListener(MAIN_CHAT_INSERT_CODE_EVENT, onInsertCode);
        return () => window.removeEventListener(MAIN_CHAT_INSERT_CODE_EVENT, onInsertCode);
    }, [input]);

    useEffect(() => {
        const applyDraft = (text) => {
            if (!text) return;
            setInput(text);
            shouldResetHeightRef.current = true;
            requestAnimationFrame(() => {
                const ta = textareaRef.current;
                if (!ta) return;
                ta.focus({ preventScroll: true });
                const end = text.length;
                ta.selectionStart = end;
                ta.selectionEnd = end;
                syncTextareaHeight(ta, 0);
            });
        };
        const stored = findingChatDraft || takeFindingChatDraft();
        const marker = stored ? `${stored.xrayJobType || ""}\n${stored.prompt || ""}` : "";
        if (stored && appliedFindingDraftRef.current !== marker) {
            appliedFindingDraftRef.current = marker;
            if (findingChatDraft) clearFindingChatDraft();
            if (stored.xrayJobType || stored.json != null) {
                pendingXraySubmitRef.current = {
                    xrayJobType: stored.xrayJobType || "",
                    json: stored.json,
                };
            }
            if (stored.prompt) applyDraft(stored.prompt);
        }
        const onDraft = (e) => applyDraft(e?.detail?.text);
        window.addEventListener(MAIN_CHAT_SET_DRAFT_EVENT, onDraft);
        return () => window.removeEventListener(MAIN_CHAT_SET_DRAFT_EVENT, onDraft);
    }, [findingChatDraft]);

    useEffect(() => {
        if (!enableAtMentions) return;
        if (mentionStart == null) return;
        const onDown = (ev) => {
            if (textareaRef.current?.contains(ev.target)) return;
            if (mentionDropdownRef.current?.contains(ev.target)) return;
            const ta = textareaRef.current;
            if (!ta) return;
            const caret = ta.selectionStart;
            const before = input.slice(0, mentionStart);
            const after = input.slice(caret);
            setInput(before + after);
            setMentionStart(null);
            setMentionQuery("");
            setMentionActiveIdx(0);
            requestAnimationFrame(() => {
                if (textareaRef.current) {
                    const pos = before.length;
                    textareaRef.current.selectionStart = pos;
                    textareaRef.current.selectionEnd = pos;
                }
            });
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [mentionStart, input, enableAtMentions]);

    useEffect(() => {
        const userPrompt = typeof window !== "undefined" ? localStorage.getItem("user-prompt") : "";
        if (userPrompt) {
            setInput(userPrompt);
            localStorage.removeItem("user-prompt");
        }
    }, []);

    // Reset textarea to default size when all files are cleared
    useEffect(() => {
        if (selectedFiles.length === 0 && textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
    }, [selectedFiles.length]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          console.warn("Speech recognition not supported in this browser.");
          return;
        }
      
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";
      
        recognition.onresult = (event) => {
          let finalTranscript = "";
          let interimTranscript = "";
      
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) finalTranscript += transcript + " ";
            else interimTranscript += transcript;
          }
      
          if (finalTranscript) {
            accumulatedTranscriptRef.current += finalTranscript;
            lastInterimRef.current = "";
          }
      
          if (isRecordingRef.current) {
            const baseText = baseInputRef.current.trim();
            const accumulated = accumulatedTranscriptRef.current.trim();
            const interim = interimTranscript.trim();
      
            const combined = [baseText, accumulated, interim].filter(Boolean).join(" ");
            setInput(combined);
          }
        };
      
        recognition.onerror = (event) => {
          console.error("Speech recognition error:", event.error);
      
          switch (event.error) {
            case "no-speech":
              stopRecognition();
              finalizeTranscript();
              break;
            case "not-allowed":
              showNotificationGlobal(
                "Microphone permission denied. Please enable microphone access.",
                "error",
                "topCenter"
              );
              stopRecognition();
              break;
            case "network":
              showNotificationGlobal("Network error. Please check your connection.", "error", "topCenter");
              stopRecognition();
              break;
            default:
              showNotificationGlobal("Speech recognition error. Please try again.", "error", "topCenter");
              stopRecognition();
              break;
          }
        };
      
        recognition.onend = () => {
          // Finalize transcript if stopped
          if (!isRecordingRef.current) {
            finalizeTranscript();
            return;
          }
      
          // Auto-restart if still recording
          try {
            recognition.start();
          } catch (err) {
            console.error("Error restarting recognition:", err);
            stopRecognition();
            finalizeTranscript();
          }
        };
      
        const stopRecognition = () => {
          isRecordingRef.current = false;
          setIsRecording(false);
          try {
            recognition.stop();
          } catch (err) {
            console.warn("Error stopping recognition:", err);
          }
        };
      
        const finalizeTranscript = () => {
          const baseText = baseInputRef.current.trim();
          const accumulated = accumulatedTranscriptRef.current.trim();
          if (accumulated) {
            const newText = [baseText, accumulated].filter(Boolean).join(" ");
            setInput(newText);
            baseInputRef.current = newText;
          }
          accumulatedTranscriptRef.current = "";
          lastInterimRef.current = "";
        };
      
        recognitionRef.current = recognition;
      
        return () => {
          try {
            recognition.stop();
          } catch {
            /* ignore */
          }
        };
      }, []);
      
    useEffect(() => {
        setCharIndex(0);
        setIsDeleting(false);
        setAnimatedPlaceholder("");
    }, [selectedAppType]);
    useEffect(() => {
        if (input) return; // Don't animate if user is typing

        const currentText = selectedAppType === "PLAN"
            ? "Describe what you want to plan and build..."
            : selectedAppType === "ASK"
            ? "Ask SuperAgentX anything about your app..."
            : "Ask SuperAgentX to create your app, agents...";
        const typingSpeed = isDeleting ? 50 : 100;
        const pauseBeforeDelete = 3000; // Pause before deleting

        const timer = setTimeout(() => {
            if (isStreaming) {
                setAnimatedPlaceholder(currentText);
                return;
            };
            if (!isDeleting && charIndex < currentText.length) {
                // Typing forward
                setAnimatedPlaceholder(currentText.substring(0, charIndex + 1));
                setCharIndex(charIndex + 1);
            } else if (!isDeleting && charIndex === currentText.length) {
                // Reached end, pause then start deleting
                setTimeout(() => setIsDeleting(true), pauseBeforeDelete);
            } else if (isDeleting && charIndex > 0) {
                // Deleting backward
                setAnimatedPlaceholder(currentText.substring(0, charIndex - 1));
                setCharIndex(charIndex - 1);
            } else if (isDeleting && charIndex === 0) {
                // Finished deleting, move to next text
                setIsDeleting(false);
            }
        }, typingSpeed);

        return () => clearTimeout(timer);
    }, [charIndex, isDeleting, input, isStreaming, selectedAppType]);

    // Add hover animation styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .send-btn-hover {
                transition: all 0.2s ease;
            }
            
            .send-btn-hover:not(:disabled):hover {
                transform: scale(1.1);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }
            
            .send-btn-hover:not(:disabled):active {
                transform: scale(0.95);
            }

            .animated-placeholder {
                position: absolute;
                top: 20px;
                left: 20px;
                color: #94a3b8;
                pointer-events: none;
                font-size: 16px;
                line-height: 1.5;
                font-family: inherit;
            }

            .cursor-blink::after {
                content: '|';
                animation: blink 1s infinite;
                margin-left: 2px;
            }

            @keyframes blink {
                0%, 50% { opacity: 1; }
                51%, 100% { opacity: 0; }
            }

            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const APP_TYPES_SMALL = [
        { value: "APP", label: "App", icon: "fa-solid fa-mobile-screen" },
        { value: "APP_AGENT", label: "App + Agents", icon: "fa-solid fa-user-astronaut" },
        { value: "AGENT", label: "Agents", icon: "fa-solid fa-infinity" },
        { value: "PLAN", label: "Plan", icon: "fa-solid fa-list-check" },
        { value: "WEBSITE", label: "Website", icon: "fa-solid fa-globe" },
        { value: "ASK", label: "Ask", icon: "fa-regular fa-circle-question" }
    ];

    const APP_TYPES = [
        { value: "AGENT", label: "Agents", icon: "fa-solid fa-infinity" },
        { value: "PLAN", label: "Plan", icon: "fa-solid fa-list-check" },
        { value: "ASK", label: "Ask", icon: "fa-regular fa-circle-question" },
        { value: "APP", label: "App", icon: "fa-solid fa-mobile-screen", premium: true },
        { value: "APP_AGENT", label: "App + Agents", icon: "fa-solid fa-user-astronaut", premium: true },
        { value: "WEBSITE", label: "Website", icon: "fa-solid fa-globe", premium: true }
    ];

    
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
    
        const currentCount = selectedFiles.length;
        const newCount = files.length;
    
        // 1️⃣ Check max file count
        if (currentCount + newCount > CODER_MAX_FILES_UPLOAD) {
            showNotificationGlobal(
                `Maximum ${CODER_MAX_FILES_UPLOAD} files allowed. Remove some files first.`,
                'error',
                'topCenter'
            );
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
    
        // 2️⃣ Calculate total size (already uploaded + new uploads)
        let existingTotalSize = selectedFiles.reduce((acc, f) => acc + f.file.size, 0);
        let newFilesTotalSize = files.reduce((acc, f) => acc + f.size, 0);
        let combinedSize = existingTotalSize + newFilesTotalSize;
    
        if (combinedSize > CODER_MAX_FILE_SIZE_UPLOAD) {
            showNotificationGlobal(
                `Total files size must be ${CODER_MAX_FILE_SIZE_UPLOAD / 1024 / 1024} MB or less.`,
                'error',
                'topCenter'
            );
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
    
        const filePromises = [];
    
        files.forEach((file) => {
            const isImage = file.type.startsWith("image/");
            const isPdf = file.type === "application/pdf";
            if (!isImage && !isPdf) {
                showNotificationGlobal(
                    `${file.name}: Please upload a valid image or PDF`,
                    'error',
                    'topCenter'
                );
                return;
            }
    
            filePromises.push(
                new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const dataUrl = reader?.result;
                        const base64 = dataUrl?.split(',')[1];
                        resolve({
                            file,
                            preview: dataUrl,
                            base64String: base64,
                            type: file.type
                        });
                    };
                    reader.readAsDataURL(file);
                })
            );
        });
    
        Promise.all(filePromises).then((newFileObjects) => {
            if (newFileObjects.length > 0) {
                setSelectedFiles((prev) => [...prev, ...newFileObjects]);
            }
        });
    
        // Reset input
        if (fileInputRef.current) fileInputRef.current.value = '';
    };
    
    
    

    const handleImageButtonClick = () => {
        if (selectedFiles.length > 0) {
            // If files exist, clicking the button opens file picker to add more (if under limit)
            if (selectedFiles.length >= CODER_MAX_FILES_UPLOAD) {
                showNotificationGlobal("Maximum " + CODER_MAX_FILES_UPLOAD + " files allowed. Please remove some files first.", 'error', 'topCenter');
                return;
            }
        }
        fileInputRef.current?.click();
    };

    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles((prev) => {
            const newFiles = prev.filter((_, index) => index !== indexToRemove);
            // Reset textarea to default size when all files are cleared
            if (newFiles.length === 0 && textareaRef.current) {
                textareaRef.current.style.height = "auto";
            }
            return newFiles;
        });
    };

    const handleRemoveMentionToken = (token, startIndex, endIndex) => {
        if (!enableAtMentions || !token) return;

        setInput((prev) => {
            let start = Number.isInteger(startIndex) ? startIndex : prev.indexOf(token);
            if (start < 0) return prev;

            let end = Number.isInteger(endIndex) ? endIndex : start + token.length;
            if (prev.slice(end, end + 1) === "\n") {
                end += 1;
            }

            const next = `${prev.slice(0, start)}${prev.slice(end)}`;
            setSelectedMentionTokens((tokens) =>
                tokens.filter((item) => item !== token || next.includes(item))
            );

            requestAnimationFrame(() => {
                const ta = textareaRef.current;
                if (!ta) return;
                const pos = Math.min(start, next.length);
                ta.focus();
                ta.selectionStart = pos;
                ta.selectionEnd = pos;
                syncMentionFromCaret(next, pos);
            });

            return next;
        });
    };

    const handleVoiceRecord = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (!SpeechRecognition) {
            showNotificationGlobal("Speech recognition is not supported in your browser.", 'error', 'topCenter');
            return;
        }

        if (!recognitionRef.current) {
            showNotificationGlobal("Speech recognition is not initialized. Please refresh the page.", 'error', 'topCenter');
            return;
        }

        if (isRecording) {
            // Stop recording
            isRecordingRef.current = false;
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch (error) {
                    console.error('Error stopping recognition:', error);
                }
            }
            setIsRecording(false);
            
            // Finalize the input with accumulated transcript
            if (accumulatedTranscriptRef.current.trim()) {
                const newText = baseInputRef.current.trim() + 
                    (baseInputRef.current.trim() && accumulatedTranscriptRef.current.trim() ? ' ' : '') + 
                    accumulatedTranscriptRef.current.trim();
                setInput(newText.trim());
                baseInputRef.current = newText.trim();
            }
            accumulatedTranscriptRef.current = "";
            lastInterimRef.current = "";
        } else {
            // Start recording - save current input as base and reset accumulated transcript
            baseInputRef.current = input.trim();
            accumulatedTranscriptRef.current = "";
            lastInterimRef.current = "";
            isRecordingRef.current = true;
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.start();
                    setIsRecording(true);
                } catch (error) {
                    console.error('Error starting speech recognition:', error);
                    isRecordingRef.current = false;
                    setIsRecording(false);
                    if (error.message && error.message.includes('already started')) {
                        // Recognition is already running, just update state
                        setIsRecording(true);
                        isRecordingRef.current = true;
                    } else {
                        showNotificationGlobal("Failed to start voice recording. Please try again.", 'error', 'topCenter');
                    }
                }
            }
        }
    };

    const handleSubmit = (e) => {
        e?.preventDefault();
      
        if (isStreaming) {
          onStop?.();
          return;
        }
      
        const prompt = input.trim();
        const files = selectedFiles;
      
        //nothing entered
        if (!prompt && files.length === 0) {
          showNotificationGlobal("Enter prompt or upload file", "error", "topCenter");
          return;
        }
      
        // if user not logged in
        if (!localStorage.getItem(ACCESS_TOKEN)) {
          localStorage.setItem("user-prompt", prompt);
          showNotificationGlobal("Please login to continue", "info", "topCenter");
          onSendClick?.();
          return;
        }
      
        // ❗ character limit check (only for prompt case)
        if (prompt && prompt.length < defaultCharacterLimit) {
          showNotificationGlobal(
            messages.characterLimit.replace("%s", defaultCharacterLimit),
            "error",
            "topCenter"
          );
          return;
        }

        let newPrompt = prompt;

        if (selectedPalette) {
            newPrompt = newPrompt + "\n\nUse the following color palette: " + selectedPalette?.colors?.join(", ");
        }
      
        // 🟢 valid → submit
        const submitData = {
          appType: selectedAppType,
          text: newPrompt,
          uploadedFiles: files,
          stackId: selectedStackId || null
        };
        const pendingXray = pendingXraySubmitRef.current;
        if (pendingXray?.xrayJobType) {
          submitData.xrayJobType = pendingXray.xrayJobType;
        }
        if (pendingXray && pendingXray.json != null) {
          submitData.json = pendingXray.json;
        }
        pendingXraySubmitRef.current = null;
      
        setIsRecording(false);
        recognitionRef.current?.stop();
        onSubmit?.(e, submitData);
      
        // clear input + file after submit
        setInput("");
        setSelectedFiles([]);
        setSelectedMentionTokens([]);
        setMentionStart(null);
        setMentionQuery("");
        setMentionActiveIdx(0);
        setSelectedPalette(null);
        shouldResetHeightRef.current = true;
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    handleSubmitRef.current = handleSubmit;

    const insertCodeMention = (path) => {
        if (!enableAtMentions) return;
        const ta = textareaRef.current;
        if (!ta || mentionStart == null || !path) return;
        const caret = ta.selectionStart ?? input.length;
        // Always put the next text on a new line after adding a badge.
        const insertText = `@${path}\n`;
        const token = `@${path}`;

        // Replace the typed "@query" fragment [mentionStart .. caret] with the mention token.
        ta.focus();
        ta.setRangeText(insertText, mentionStart, caret, "end");
        setInput(ta.value);
        setSelectedMentionTokens((prev) => (prev.includes(token) ? prev : [...prev, token]));
        setMentionStart(null);
        setMentionQuery("");
        setMentionActiveIdx(0);
        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            const pos = mentionStart + insertText.length;
            textareaRef.current.selectionStart = pos;
            textareaRef.current.selectionEnd = pos;
            syncMentionFromCaret(textareaRef.current.value, pos);
        });
    };

    const handleKeyDown = (e) => {
        const ta = textareaRef.current;
        const caret = ta?.selectionStart ?? input.length;
        const selectionEnd = ta?.selectionEnd ?? caret;

        // Backspace should remove the whole @badge token (one keypress)
        if (enableAtMentions && e.key === "Backspace" && ta && caret === selectionEnd) {
            const leftCaret = caret;
            const left = input.slice(0, leftCaret);
            // If caret is after spaces/newlines, treat it as "after token".
            // We auto-insert "\n" after a badge, so if caret is on the next line,
            // remove the whole badge + newline in one backspace.
            const trimmedRight = left.replace(/[ \t]+$/, "");
            const endsWithNewline = trimmedRight.endsWith("\n");
            const leftForTokenMatch = endsWithNewline
                ? trimmedRight.slice(0, -1).replace(/[ \t]+$/, "")
                : trimmedRight;

            // Match either "@path" OR "@path (12-23)" at end of left side
            const tokenRe = /@([^\s]+)(\s*\(\s*\d+\s*-\s*\d+\s*\))?$/;
            const m = leftForTokenMatch.match(tokenRe);
            if (m) {
                e.preventDefault();
                const tokenStart = leftForTokenMatch.lastIndexOf("@");
                const before = input.slice(0, tokenStart);
                // remove any trailing "\n" after the badge too
                const after = input.slice(leftCaret);
                // Remove any extra leading space left behind
                const next = (before + after).replace(/\s{2,}/g, " ");
                setInput(next);
                setMentionStart(null);
                setMentionQuery("");
                setMentionActiveIdx(0);
                requestAnimationFrame(() => {
                    if (!textareaRef.current) return;
                    const pos = Math.max(0, tokenStart);
                    textareaRef.current.selectionStart = pos;
                    textareaRef.current.selectionEnd = pos;
                });
                return;
            }
        }

        if (enableAtMentions && mentionStart != null) {
            if (e.key === "ArrowDown" && mentionSuggestions.length > 0) {
                e.preventDefault();
                setMentionActiveIdx((i) => Math.min(i + 1, mentionSuggestions.length - 1));
                return;
            }
            if (e.key === "ArrowUp" && mentionSuggestions.length > 0) {
                e.preventDefault();
                setMentionActiveIdx((i) => Math.max(i - 1, 0));
                return;
            }
            if (
                (e.key === "Enter" || e.key === "Tab") &&
                !e.shiftKey &&
                mentionSuggestions.length > 0
            ) {
                e.preventDefault();
                const pick =
                    mentionSuggestions[mentionActiveIdx] ?? mentionSuggestions[0];
                insertCodeMention(pick);
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                if (!ta) return;
                const before = input.slice(0, mentionStart);
                const after = input.slice(caret);
                setInput(before + after);
                setMentionStart(null);
                setMentionQuery("");
                setMentionActiveIdx(0);
                requestAnimationFrame(() => {
                    if (textareaRef.current) {
                        const pos = before.length;
                        textareaRef.current.selectionStart = pos;
                        textareaRef.current.selectionEnd = pos;
                    }
                });
                return;
            }
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const injectToPromptInTextarea = (palette) => {
            setSelectedPalette(palette);
    };

    const syncTextareaHeight = (ta, scrollTop = ta?.scrollTop ?? 0) => {
        if (!ta) return;
        ta.style.height = 'auto';
        const px = Math.min(ta.scrollHeight, 240) + 'px';
        ta.style.height = px;
        if (enableAtMentions && highlightLayerRef.current) {
            highlightLayerRef.current.scrollTop = scrollTop;
        }
    };

    useLayoutEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        const reset = shouldResetHeightRef.current;
        if (reset) {
            shouldResetHeightRef.current = false;
            ta.scrollTop = 0;
        }
        syncTextareaHeight(ta, reset ? 0 : ta.scrollTop);
        if (reset) ta.focus({ preventScroll: true });
    }, [input, selectedFiles.length, enableAtMentions]);

    const handleTextareaInput = (e) => {
        syncTextareaHeight(e.target, e.target.scrollTop);
    };

    const getSelectedType = () => {
        const allTypes = [...APP_TYPES_SMALL, ...APP_TYPES];
        return allTypes.find(type => type.value === selectedAppType);
    };

    const getSelectedIcon = () => getSelectedType()?.icon || "fa-solid fa-robot";

    const appTypeMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (appTypeMenuRef.current && !appTypeMenuRef.current.contains(event.target)) {
                setIsAppTypeMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const selectStack = (id) => {
        setSelectedStackId(id);        // "" means platform default
        setIsStackMenuOpen(false);
    };

    const loadStacks = async () => {
        setStacksLoading(true);
        setStacksError(false);
        try {
            const res = await StackService.getAllStacks();
            if (res.status === API_RESPONSE.SUCCESS) setStacks(res.stacks || []);
            else setStacksError(true);
        } catch (e) {
            console.error("Could not load stacks", e);
            setStacksError(true);
        } finally {
            setStacksLoading(false);
        }
    };

    const toggleStackMenu = () => {
        const next = !isStackMenuOpen;
        setIsStackMenuOpen(next);
        if (next && stacks.length === 0) loadStacks();
    };


    useEffect(() => {
        if (!isStackMenuOpen) return;
        const close = (e) => {
            if (stackMenuRef.current && !stackMenuRef.current.contains(e.target)) setIsStackMenuOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [isStackMenuOpen]);

    const selectAppType = (typeValue) => {
        const typeMeta = [...APP_TYPES_SMALL, ...APP_TYPES].find((type) => type.value === typeValue);
        if (typeMeta?.premium && isPocTenant()) {
            setPremiumDialog({ show: true, featureName: typeMeta.label });
            setIsAppTypeMenuOpen(false);
            return;
        }

        setSelectedAppType(typeValue);
        onAppTypeChange?.(typeValue);
    };

    const handleAppTypeChange = async (typeValue) => {
        selectAppType(typeValue);
        if (typeValue !== "AGENT") return;

        const isAuthenticated = localStorage.getItem(ACCESS_TOKEN);
    
        if (!isAuthenticated) {
            showNotificationGlobal("Please login to continue", 'info', 'topCenter');
            onSendClick?.();
            return;
        }
    
        // Navigate directly without hash artifacts
        navigate('/app/agent', { replace: true });
    };

    const isPlanMode = selectedAppType === "PLAN";
    const isAskMode = selectedAppType === "ASK";
    useEffect(() => {
        if (!premiumDialog.show) return undefined;
        const onKey = (event) => {
            if (event.key === "Escape") setPremiumDialog({ show: false, featureName: "" });
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKey);
        };
    }, [premiumDialog.show]);

    const closePremiumDialog = () => setPremiumDialog({ show: false, featureName: "" });
    const isActive = isStreaming || input.trim();
    const textareaPaddingBottom = selectedFiles.length > 0 ? '120px' : '20px';

    return (
        <div
            className={`sa-chat-composer ${isPlanMode ? "sa-chat-composer--plan" : ""} ${isAskMode ? "sa-chat-composer--ask" : ""}`}
            style={{
                ...styles.container,
            }}
        >
            {(isPlanMode || isAskMode) && (
                <div className={`sa-composer-mode-banner sa-composer-mode-banner--${isPlanMode ? "plan" : "ask"}`}>
                    <i className={isPlanMode ? "fa-solid fa-list-check" : "fa-regular fa-circle-question"} aria-hidden="true" />
                    <span>
                        {isPlanMode
                            ? "Plan mode is active — your next message will start a planning session"
                            : "Ask mode is active"}
                    </span>
                </div>
            )}
            {premiumDialog.show
                ? createPortal(
                    <div
                        role="presentation"
                        onClick={closePremiumDialog}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 2400,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 20,
                            background: "#f4f7fb",
                        }}
                    >
                        <div
                            role="dialog"
                            aria-modal="true"
                            aria-label={`Unlock ${premiumDialog.featureName || "this feature"}`}
                            onClick={(event) => event.stopPropagation()}
                            style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto" }}
                        >
                            <PremiumFeatureLocked
                                compact
                                featureName={premiumDialog.featureName}
                                onClose={closePremiumDialog}
                            />
                        </div>
                    </div>,
                    document.body
                )
                : null}
            <div style={styles.textareaWrapper}>
                {enableAtMentions && (
                    <div
                        ref={highlightLayerRef}
                        aria-hidden="true"
                        style={{
                            ...styles.textareaHighlightLayer,
                            paddingBottom: textareaPaddingBottom,
                        }}
                    >
                        {highlightParts.map((p, idx) =>
                            p.type === "mention" ? (
                                <span key={`${p.value}-${idx}`} style={styles.inlineMentionChip}>
                                    <button
                                        type="button"
                                        style={styles.inlineMentionChipClose}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleRemoveMentionToken(p.value, p.start, p.end);
                                        }}
                                        aria-label={`Remove ${p.label}`}
                                        title="Remove from chat"
                                    >
                                        <i className="fa-solid fa-xmark" style={{ fontSize: 11 }} />
                                    </button>
                                    <span className="">{p.label}</span>
                                </span>
                            ) : (
                                <span key={`t-${idx}`}>{p.value}</span>
                            )
                        )}
                    </div>
                )}
                {!input && (
                    <div className={`animated-placeholder ${!isDeleting ? 'cursor-blink' : ''}`}>
                        {animatedPlaceholder}
                    </div>
                )}
                {selectedFiles.length > 0 && (
                    <div style={styles.filesPreviewContainer}>
                        {selectedFiles.map((fileObj, index) => (
                            <div key={index} style={styles.filePreviewItem}>
                                {fileObj.type.startsWith("image/") ? (
                                    <img
                                        title={fileObj.file.name}
                                        src={fileObj.preview}
                                        alt={`Preview ${index + 1}`}
                                        style={styles.imagePreviewImg}
                                    />
                                ) : (
                                    <div style={styles.pdfPreviewContainer} title={fileObj.file.name}>
                                        <i className="fa-solid fa-file-pdf" style={{ fontSize: '32px', color: '#ff8686' }}></i>
                                        <div style={styles.pdfFileName}>{fileObj.file.name}</div>
                                    </div>
                                )}
                                <button
                                    onClick={() => handleRemoveFile(index)}
                                    style={styles.imagePreviewRemoveBtn}
                                    title={`Remove ${fileObj.type.startsWith("image/") ? 'image' : 'PDF'}`}
                                >
                                    <i className="fa-solid fa-xmark" style={{ fontSize: '12px' }}></i>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <textarea
                    ref={textareaRef}
                    value={input}
                    className={`landing-chat-textarea ${isStreaming ? 'force-disabled' : ''}`}
                    onPaste={(e) => {
                        if (!enableAtMentions) return;
                        const text = e.clipboardData?.getData("text/plain") || "";
                        // If user pasted Monaco copy payload:
                        // "@path/to/file (1-21)\n<code...>"
                        // only insert the header reference, not the code body.
                        const match = text.match(/^@([^\s]+)\s*\(\s*(\d+)\s*-\s*(\d+)\s*\)\s*\n/s);
                        if (!match) return;

                        const header = `@${match[1]} (${match[2]} - ${match[3]})`;
                        e.preventDefault();

                        const ta = textareaRef.current;
                        if (!ta) return;
                        const start = ta.selectionStart ?? input.length;
                        const end = ta.selectionEnd ?? input.length;
                        // Always put the next text on a new line after adding a badge.
                        const insertText = `${header}\n`;

                        // Insert and move caret to the end of inserted text.
                        ta.focus();
                        ta.setRangeText(insertText, start, end, "end");
                        setInput(ta.value);
                        setSelectedMentionTokens((prev) =>
                            prev.includes(header) ? prev : [...prev, header]
                        );

                        // Ensure mention dropdown state stays in sync with caret.
                        requestAnimationFrame(() => {
                            const pos = start + insertText.length;
                            ta.selectionStart = pos;
                            ta.selectionEnd = pos;
                            syncMentionFromCaret(ta.value, pos);
                        });
                    }}
                    onScroll={(e) => {
                        if (!enableAtMentions) return;
                        if (highlightLayerRef.current) {
                            highlightLayerRef.current.scrollTop = e.target.scrollTop;
                        }
                    }}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        const caret = e.target.selectionStart;
                        // Stop recording if user types anything
                        if (isRecording || isRecordingRef.current) {
                            isRecordingRef.current = false;
                            if (recognitionRef.current) {
                                try {
                                    recognitionRef.current.stop();
                                } catch (error) {
                                    console.error('Error stopping recognition:', error);
                                }
                            }
                            setIsRecording(false);
                            
                            // Clear accumulated transcript since user is typing manually
                            accumulatedTranscriptRef.current = "";
                            lastInterimRef.current = "";
                            // Update base input to current value
                            baseInputRef.current = newValue.trim();
                        }
                        setInput(newValue);
                        if (enableAtMentions) syncMentionFromCaret(newValue, caret);
                    }}
                    onSelect={(e) => {
                        if (enableAtMentions) syncMentionFromCaret(e.target.value, e.target.selectionStart);
                    }}
                    onKeyDown={handleKeyDown}
                    onInput={handleTextareaInput}
                    placeholder=""
                    style={{
                        ...styles.textarea,
                        ...(enableAtMentions ? styles.textareaMentionMode : {}),
                        paddingBottom: textareaPaddingBottom
                    }}
                />
                {enableAtMentions && mentionStart != null && (
                    <div
                        ref={mentionDropdownRef}
                        style={styles.codeMentionDropdown}
                    >
                        {mentionSuggestions.length === 0 ? (
                            <div style={styles.codeMentionEmpty}>
                                {codeViewPaths.length === 0
                                    ? "Open Code view and load project files to mention them with @."
                                    : "No files match your search."}
                            </div>
                        ) : (
                            mentionSuggestions.map((path, idx) => (
                                <button
                                    key={path}
                                    type="button"
                                    style={{
                                        ...styles.codeMentionItem,
                                        ...(idx === mentionActiveIdx
                                            ? styles.codeMentionItemActive
                                            : {})
                                    }}
                                    onMouseEnter={() => setMentionActiveIdx(idx)}
                                    onMouseDown={(ev) => {
                                        ev.preventDefault();
                                        insertCodeMention(path);
                                    }}
                                >
                                    {path}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>

            <div className="sa-composer-toolbar">
                <div className="sa-composer-toolbar-left">
                    <div
                        ref={appTypeMenuRef}
                        style={styles.appTypeDropdownWrapper}
                    >
                        <button
                            type="button"
                            id="app-type-dropdown"
                            style={styles.dropdownToggle}
                            className="border-0 shadow-none dropdown-toggle-hover"
                            onClick={() => setIsAppTypeMenuOpen((prev) => !prev)}
                        >
                            <i className={`${getSelectedIcon()} me-1 font11`}></i>
                            <span className="font11">
                                {getSelectedType()?.label || "App"}
                            </span>
                            {isPocTenant() && getSelectedType()?.premium ? (
                                <i
                                    className="fa-solid fa-crown font10"
                                    title="Premium"
                                    aria-label="Premium"
                                    style={styles.premiumIcon}
                                ></i>
                            ) : null}
                            <i
                                className={`fa-solid ${
                                    isAppTypeMenuOpen ? "fa-chevron-up" : "fa-chevron-down"
                                } ms-1 font10`}
                            ></i>
                        </button>

                        {isAppTypeMenuOpen && (
                            <div style={styles.dropdownMenu}>
                                {(window.location.pathname.includes("/project") ? APP_TYPES_SMALL : APP_TYPES).map((type) => {
                                    const isActive = selectedAppType === type.value;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            className="font11 text-start w-100"
                                            style={{
                                                ...styles.dropdownMenuItem,
                                                ...(isActive ? styles.dropdownMenuItemActive : {})
                                            }}
                                            onClick={() => {
                                                handleAppTypeChange(type.value);
                                                setIsAppTypeMenuOpen(false);
                                            }}
                                        >
                                            <i className={`${type.icon} font11`}></i>
                                            <span style={{ flex: 1 }}>{type.label}</span>
                                            {isPocTenant() && type.premium ? (
                                                <i
                                                    className="fa-solid fa-crown font10"
                                                    title="Premium"
                                                    aria-label="Premium"
                                                    style={styles.premiumIcon}
                                                ></i>
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <ColorPaletteSelector
                        selectedPalette={selectedPalette}
                        onPaletteSelect={injectToPromptInTextarea}
                    />
                </div>



                <div className="sa-composer-toolbar-right">
                    {!window.location.pathname.includes("/project") && (
                        <div ref={stackMenuRef} style={styles.appTypeDropdownWrapper}>
                            <StackSelector
                                open={isStackMenuOpen}
                                loading={stacksLoading}
                                error={stacksError}
                                stacks={stacks}
                                selectedStackId={selectedStackId}
                                disabled={isStreaming}
                                onToggle={toggleStackMenu}
                                onClose={() => setIsStackMenuOpen(false)}
                                onSelect={selectStack}
                                onRetry={loadStacks}
                            />
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*, application/pdf"
                        onChange={handleImageUpload}
                        multiple
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        style={{
                            ...styles.attachmentBtn,
                            backgroundColor: selectedFiles.length > 0 ? '#e3f2fd' : 'transparent',
                            borderColor: selectedFiles.length > 0 ? '#2196f3' : '#e2e8f0',
                            color: selectedFiles.length > 0 ? '#2196f3' : '#64748b',
                            position: 'relative'
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleImageButtonClick}
                        className={isStreaming || selectedFiles.length >= CODER_MAX_FILES_UPLOAD ? "force-disabled" : ""}
                        disabled={isStreaming || selectedFiles.length >= CODER_MAX_FILES_UPLOAD}
                        title={selectedFiles.length >= CODER_MAX_FILES_UPLOAD ? "Maximum " + CODER_MAX_FILES_UPLOAD + " files reached" : selectedFiles.length > 0 ? `Add more files (${selectedFiles.length}/${CODER_MAX_FILES_UPLOAD})` : "Upload image or PDF"}
                    >
                        <i className="fa-solid fa-image" style={{ fontSize: '13px' }}></i>
                        {selectedFiles.length > 0 && (
                            <span style={styles.fileCountBadge}>{selectedFiles.length}</span>
                        )}
                    </button>

                    <button
                        type="button"
                        className=""
                        style={{
                            ...styles.attachmentBtn,
                            backgroundColor: isRecording ? '#fee2e2' : 'transparent',
                            borderColor: isRecording ? '#ef4444' : '#e2e8f0',
                            color: isRecording ? '#ef4444' : '#64748b',
                            animation: isRecording ? 'pulse 1.5s infinite' : 'none'
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleVoiceRecord}
                        disabled={isStreaming}
                        title={isRecording ? "Stop recording" : "Start voice recording"}
                    >
                        <i className={`fa-solid fa-microphone${isRecording ? '' : '-slash'}`} style={{ fontSize: '13px' }}></i>
                    </button>

                    <button
                        type="button"
                        className="send-btn-hover"
                        style={{
                            ...styles.sendBtn,
                            background: isActive || selectedFiles.length > 0 ? (isStreaming ? '#000000' : 'var(--sx-background-btn)') : '#dfe4e8',
                            cursor: isActive || selectedFiles.length > 0 ? 'pointer' : 'not-allowed',
                            opacity: isActive || selectedFiles.length > 0 ? 1 : 0.6
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSubmit}
                        disabled={!isActive && selectedFiles.length === 0}
                    >
                        <i className={`fa-solid fa-${isStreaming ? 'stop' : 'paper-plane'}`}
                            style={{ fontSize: '12px' }}
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '25px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'visible'
    },
    textareaWrapper: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden'
    },
    textareaHighlightLayer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 3,
        padding: '20px',
        paddingRight: '20px',
        overflow: 'hidden',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        fontSize: '16px',
        lineHeight: '1.5',
        fontFamily: 'inherit',
        color: '#1e293b',
        pointerEvents: 'none',
        boxSizing: 'border-box'
    },
    inlineMentionChip: {
        display: 'inline',
        padding: '4px 10px',
        margin: 0,
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#2c3e50',
        // Visual separation without affecting caret metrics
        boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.35), 0 0 0 2px rgba(255, 255, 255, 0.85)',
        color: '#ffffff',
        fontSize: '14px',
        lineHeight: 1.4,
        verticalAlign: 'baseline',
        whiteSpace: 'nowrap'
    },
    inlineMentionChipClose: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '14px',
        height: '14px',
        padding: 0,
        marginRight: '8px',
        border: 'none',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        color: '#cbd5e1',
        cursor: 'pointer',
        pointerEvents: 'auto',
        verticalAlign: 'middle'
    },
    codeMentionDropdown: {
        position: 'absolute',
        left: '16px',
        right: '16px',
        bottom: '100%',
        marginBottom: '8px',
        maxHeight: '220px',
        overflowY: 'auto',
        zIndex: 100,
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxShadow: '0 -8px 32px rgba(15, 23, 42, 0.16)',
        padding: '6px'
    },
    codeMentionItem: {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'transparent',
        color: '#0f172a',
        fontSize: '13px',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    codeMentionItemActive: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8'
    },
    codeMentionEmpty: {
        padding: '10px 12px',
        fontSize: '13px',
        color: '#64748b',
        lineHeight: 1.4
    },
    textarea: {
        border: 'none',
        outline: 'none',
        fontSize: '16px',
        padding: '20px',
        paddingRight: '20px', // Extra padding for scrollbar
        backgroundColor: 'transparent',
        color: '#1e293b',
        position: 'relative',
        zIndex: 2,
        resize: 'none',
        minHeight: '80px',
        maxHeight: '240px',
        overflowY: 'auto',
        lineHeight: '1.5',
        fontFamily: 'inherit',
        width: '100%',
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        borderBottomLeftRadius: '0px',
        borderBottomRightRadius: '0px'
    },
    textareaMentionMode: {
        color: 'transparent',
        caretColor: '#1e293b'
    },
    bottomControls: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '3px 20px',
        borderTop: '1px solid #f1f5f9',
        backgroundColor: '#fafbfc',
        borderBottomLeftRadius: '25px',
        borderBottomRightRadius: '25px'
    },
    leftControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    rightControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    attachmentBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        backgroundColor: 'transparent',
        border: '1px solid #e2e8f0',
        borderRadius: '50%',
        cursor: 'pointer',
        color: '#64748b'
    },
    sendBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '35px',
        height: '35px',
        border: 'none',
        borderRadius: '50%',
        transition: 'all 0.2s ease',
        color: 'white'
    },
    appTypeDropdownWrapper: {
        position: 'relative',
        display: 'inline-block',
        zIndex: 40
    },
    dropdownToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 10px',
        backgroundColor: '#f0f3f5',
        border: '1px solid #e2e8f0',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#475569'
    },
    premiumIcon: {
        color: '#d97706',
        fontSize: '10px',
        flexShrink: 0
    },
    dropdownMenu: {
        position: 'absolute',
        bottom: '110%',
        left: 0,
        minWidth: '200px',
        padding: '6px',
        borderRadius: '14px',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        border: '1px solid rgba(226, 232, 240, 0.9)',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)',
        backdropFilter: 'blur(16px)',
        zIndex: 50
    },
    dropdownMenuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        padding: '6px 10px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#0f172a',
        fontSize: '11px',
        cursor: 'pointer',
        textAlign: 'left'
    },
    dropdownMenuItemActive: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8'
    },
    filesPreviewContainer: {
        position: 'absolute',
        bottom: '10px',
        left: '12px',
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        zIndex: 10,
        maxWidth: 'calc(100% - 24px)',
        pointerEvents: 'none'
    },
    filePreviewItem: {
        position: 'relative',
        width: '80px',
        height: '80px',
        borderRadius: '8px',
        border: '2px solid #e2e8f0',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        flexShrink: 0,
        pointerEvents: 'auto'
    },
    imagePreviewInTextarea: {
        position: 'absolute',
        bottom: '20px',
        left: '12px',
        width: '80px',
        height: '80px',
        borderRadius: '8px',
        border: '2px solid #e2e8f0',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10
    },
    imagePreviewImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imagePreviewRemoveBtn: {
        position: 'absolute',
        top: '-10px',
        right: '-10px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#e2e2e2',
        border: '2px solid white',
        color: '#7f7f7f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        padding: 0,
        zIndex: 11
    },
    pdfPreviewContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        padding: '8px',
        gap: '4px'
    },
    pdfFileName: {
        fontSize: '10px',
        color: '#475569',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        maxWidth: '70px'
    },
    fileCountBadge: {
        position: 'absolute',
        top: '-6px',
        right: '-6px',
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '50%',
        width: '18px',
        height: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        fontWeight: 'bold',
        border: '2px solid white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
    }
};

export default TextareaInput;